import React, { useState, useEffect, useMemo } from 'react';
import { Employee, EmployeeStatus, TimeLog, DisplayGroupSettings, NameDisplayFormat, TimeEntrySource, Schedules, Shift, PayrollSettings, DisplayGroup, LeaveEntry } from '../../shared/types.js';
import { useTimeClock } from '../hooks/useTimeClock';
import { formatDuration, formatEmployeeName, getTodaysShiftForEmployee } from '../utils.js';

interface InOutBoardProps {
    employees: Employee[];
    onSelectEmployee: (employee: Employee) => void;
    displayOptions: DisplayGroupSettings['inOutBoardOptions'];
    nameFormat?: NameDisplayFormat;
    onContextMenu: (event: React.MouseEvent, employee: Employee) => void;
    schedules: Schedules;
    shifts: Shift[];
    payrollSettings: PayrollSettings;
    displayGroups: DisplayGroup[];
    sizeIndex: number;
}

type DisplayStatus = 'IN' | 'LUNCH' | 'OUT' | 'ABSENT' | 'AUTO_OUT' | 'ON_LEAVE';

interface BoardRowData {
    employee: Employee;
    displayStatus: DisplayStatus;
    lastEntryTime: number | undefined;
    elapsed: number;
}

const STATUS_CONFIG: Record<DisplayStatus, { text: string; color: string; }> = {
    IN: { text: 'In', color: 'bg-green-500' },
    LUNCH: { text: 'Lunch', color: 'bg-yellow-500' },
    OUT: { text: 'Out', color: 'bg-gray-500' },
    AUTO_OUT: { text: 'Auto Clock Out', color: 'bg-gray-500' },
    ABSENT: { text: 'Absent', color: 'bg-red-500' },
    ON_LEAVE: { text: 'On Leave', color: 'bg-purple-500' },
};

const InOutBoard: React.FC<InOutBoardProps> = ({ employees, onSelectEmployee, displayOptions, nameFormat, onContextMenu, schedules, shifts, payrollSettings, displayGroups, sizeIndex }) => {
    const { timeLogs, leaveEntries, getActivityId } = useTimeClock();
    const [now, setNow] = useState(Date.now());
    const sizeConfig: Record<number, { p: string; font: string }> = {
        0: { p: 'p-2', font: 'text-xs' },
        1: { p: 'p-3', font: 'text-sm' },
        2: { p: 'p-4', font: 'text-base' },
		3: { p: 'p-5', font: 'text-lg' },
		4: { p: 'p-6', font: 'text-xl' },
    };
    const { p, font } = sizeConfig[sizeIndex] || sizeConfig[1];
    useEffect(() => {
        const timerId = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timerId);
    }, []);
    const boardData = useMemo<BoardRowData[]>(() => {
        return employees.map(employee => {
            const employeeLogs = timeLogs
                .filter(log => log.employeeId === employee.emp_number)
                .sort((a, b) => b.startTime - a.startTime);
            const lastLog = employeeLogs[0];
            const status = employee.status;
            let elapsed = 0;
            let lastEntryTime : number | undefined;
            const inProgressLog = employeeLogs.find(log => !log.endTime);
            if (status !== EmployeeStatus.CLOCKED_OUT && inProgressLog) {
                elapsed = now - inProgressLog.startTime;
                lastEntryTime = inProgressLog.startTime;
            } else if (lastLog) {
                lastEntryTime = lastLog.endTime || lastLog.startTime;
            }
            let displayStatus: DisplayStatus;
            const today = new Date(now);
            today.setHours(0, 0, 0, 0);
            const todayTimestamp = today.getTime();
            const dayOfWeek = today.getDay();
            const isOnLeaveToday = leaveEntries.some(le => {
                if (!le.employeeIds.includes(employee.id)) return false;
                if (todayTimestamp < le.startDate || todayTimestamp > le.endDate) return false;
                const isOrangeHRMLeave = le.id.startsWith('ohrm_');
                const skipSaturday = le.skipSaturday ?? true;
                const skipSunday = le.skipSunday ?? true;
                if (!isOrangeHRMLeave && ((skipSaturday && dayOfWeek === 6) || (skipSunday && dayOfWeek === 0))) {
                    return false;
                }
                return true;
            });
            if (isOnLeaveToday) {
                displayStatus = 'ON_LEAVE';
            } else if (status === EmployeeStatus.CLOCKED_IN) {
                displayStatus = 'IN';
            } else if (status === EmployeeStatus.ON_BREAK) {
                displayStatus = 'LUNCH';
            } else if (status === EmployeeStatus.CLOCKED_OUT) {
                const todaysShift = getTodaysShiftForEmployee(employee.id, schedules, shifts, payrollSettings, displayGroups, new Date(now), false);
                if (todaysShift) {
                    const nowTime = new Date(now);
                    const [startHours, startMinutes] = todaysShift.startTime.split(':').map(Number);
                    const shiftStartTime = new Date(nowTime.getFullYear(), nowTime.getMonth(), nowTime.getDate(), startHours, startMinutes).getTime();
                    if (now > shiftStartTime) {
                        const inActivityId = getActivityId('In');
                        const hasClockedInForShift = timeLogs.some(log => 
                            log.employeeId === employee.emp_number && 
                            log.startTime >= shiftStartTime &&
                            log.activityId === inActivityId
                        );
                        if (!hasClockedInForShift) {
                            displayStatus = 'ABSENT';
                        } else {
                            displayStatus = lastLog?.source === TimeEntrySource.AUTO ? 'AUTO_OUT' : 'OUT';
                        }
                    } else {
                        displayStatus = lastLog?.source === TimeEntrySource.AUTO ? 'AUTO_OUT' : 'OUT';
                    }
                } else {
                    displayStatus = lastLog?.source === TimeEntrySource.AUTO ? 'AUTO_OUT' : 'OUT';
                }
            } else {
                 displayStatus = 'OUT';
            }
            return {
                employee,
                displayStatus,
                lastEntryTime,
                elapsed,
            };
        });
    }, [employees, timeLogs, leaveEntries, now, schedules, shifts, payrollSettings, getActivityId, displayGroups]);
    const statusIndicator = (status: DisplayStatus) => {
        const color = STATUS_CONFIG[status].color;
        return <div className={`w-3 h-3 rounded-full ${color}`}></div>;
    };
    const formatDate = (timestamp?: number) => {
        if (!timestamp) return '';
        const d = new Date(timestamp);
        if (isNaN(d.getTime())) return '';
        const today = new Date();
        if (d.toDateString() === today.toDateString()) return "Today";
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };
    const formatTime = (timestamp?: number) => {
        if (!timestamp) return '';
        const d = new Date(timestamp);
        if (isNaN(d.getTime())) return '';
        return new Date(timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    };
	const statusColors = {
	  ABSENT: 'bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-900/60',
	  LUNCH: 'bg-yellow-100 dark:bg-yellow-900/40 hover:bg-yellow-200 dark:hover:bg-yellow-900/60',
	  IN: 'bg-green-100 dark:bg-green-900/40 hover:bg-green-200 dark:hover:bg-green-900/60',
      ON_LEAVE: 'bg-purple-100 dark:bg-purple-900/40 hover:bg-purple-200 dark:hover:bg-purple-900/60',
	};
    return (
        <div className="w-full max-w-7xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            <table className={`w-full text-left ${font} text-gray-700 dark:text-gray-300`}>
                <thead className="bg-gray-200 dark:bg-gray-700/50">
                    <tr>
                        <th className={`${p} w-12 text-center hidden sm:table-cell`}>In</th>
                        <th className={`${p}`}>Name</th>
                        {displayOptions.columns.status && <th className={`${p}`}>Status</th>}
                        {displayOptions.columns.date && <th className={`${p}`}>Date</th>}
                        {displayOptions.columns.time && <th className={`${p}`}>Time</th>}
                        {displayOptions.columns.elapsed && <th className={`${p} hidden sm:table-cell`}>Elapsed</th>}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700/50">
                    {boardData.map(data => (
                        <tr 
                            key={data.employee.id} 
                            onClick={() => onSelectEmployee(data.employee)} 
                            onContextMenu={(e) => onContextMenu(e, data.employee)}
                            className={`hover:bg-gray-100 dark:hover:bg-gray-700/50 cursor-pointer transition-colors ${statusColors[data.displayStatus] || ''}`}
                        >
                            <td className={`${p} text-center flex justify-center items-center h-full hidden sm:table-cell`}>{statusIndicator(data.displayStatus)}</td>
                            <td className={`${p} font-semibold text-gray-900 dark:text-white`}>{formatEmployeeName(data.employee, nameFormat)}</td>
                            {displayOptions.columns.status && <td className={`${p}`}>{STATUS_CONFIG[data.displayStatus].text}</td>}
                            {displayOptions.columns.date && <td className={`${p}`}>{formatDate(data.lastEntryTime)}</td>}
                            {displayOptions.columns.time && <td className={`${p}`}>{formatTime(data.lastEntryTime)}</td>}
                            {displayOptions.columns.elapsed && (
                                <td className={`${p} font-mono hidden sm:table-cell`}>{data.displayStatus === 'IN' || data.displayStatus === 'LUNCH' ? formatDuration(data.elapsed, false) : ''}</td>
                            )}
                        </tr>
                    ))}
                     {boardData.length === 0 && (
                        <tr>
                            <td colSpan={6} className="text-center p-8 text-gray-500">
                                No active employees in this group.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default InOutBoard;